import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import {
  listLocalSurveys, allStatuses, subscribe, isOnline, syncNow, clientId,
  removeLocalSurvey, enqueueOp,
} from "../lib/surveysOffline";
import { Plus, Wifi, WifiOff, RefreshCw, CheckCircle2, AlertCircle, Loader2, Trash2 } from "lucide-react";
import { toast } from "../components/ui/sonner";

/**
 * Surveys list — merges server-side surveys with locally-stored drafts.
 * Local-only drafts (offline create not yet synced) are shown alongside
 * server surveys with a sync-status badge per row.
 */
export default function SurveysPage() {
  const nav = useNavigate();
  const [serverSurveys, setServerSurveys] = useState([]);
  const [localSurveys, setLocalSurveys] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [online, setOnline] = useState(isOnline());
  const [templates, setTemplates] = useState([]);
  const [picker, setPicker] = useState({ open: false, productType: "" });
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      if (isOnline()) {
        const r = await api.get("/surveys");
        setServerSurveys(r.data || []);
      }
      setLocalSurveys(await listLocalSurveys());
      setStatusMap(await allStatuses());
    } catch (_) { /* offline / 401 */ }
    setRefreshing(false);
  };

  useEffect(() => {
    refresh();
    Promise.all([
      api.get("/survey-templates?live_only=true").catch(() => ({ data: [] })),
    ]).then(([t]) => {
      setTemplates(t.data || []);
    });
    const off = subscribe(refresh);
    const onOnline = () => { setOnline(true); refresh(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      off();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merge: prefer the local version (it's authoritative for unsynced edits)
  const localByServer = Object.fromEntries(
    localSurveys.filter((l) => l.server_id).map((l) => [l.server_id, l])
  );
  const localOnly = localSurveys.filter((l) => !l.server_id);
  const merged = [
    ...localOnly.map((l) => ({ ...l, _isLocal: true })),
    ...serverSurveys.map((s) => {
      const local = localByServer[s.id];
      return local ? { ...s, ...local, _isLocal: true, server_id: s.id } : s;
    }),
  ];

  const onCreate = async () => {
    // Refresh the template list every time the picker opens — otherwise a
    // template that was created in another tab (or just now in Settings)
    // won't be visible until the page is reloaded.
    try {
      const { data } = await api.get("/survey-templates?live_only=true");
      setTemplates(data || []);
      if ((data || []).length === 0) {
        toast.error("No live survey templates yet. Ask an admin to create one in Settings → Surveys, or toggle a draft template to LIVE.");
        return;
      }
    } catch (_) {
      if (templates.length === 0) {
        toast.error("No survey templates yet. Ask an admin to create one in Settings → Surveys.");
        return;
      }
    }
    setPicker({ open: true, productType: "" });
  };

  const startNewSurvey = (templateId) => {
    const cid = clientId();
    nav(`/surveys/new?template=${encodeURIComponent(templateId)}&cid=${encodeURIComponent(cid)}`);
  };

  const deleteSurvey = async (s) => {
    const label = s.customer_name || s.template_name || "this survey";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    const serverId = s.server_id || (!s.client_id ? s.id : null);
    // Optimistic UI: drop the row from local state immediately so the user
    // sees the deletion regardless of online/offline state.
    setServerSurveys((prev) => prev.filter((x) => x.id !== serverId && x.id !== s.id));
    setLocalSurveys((prev) => prev.filter((x) => x.client_id !== s.client_id));
    try {
      if (serverId) {
        try {
          await api.delete(`/surveys/${serverId}`);
        } catch (e) {
          if (!isOnline()) {
            await enqueueOp({ type: "delete_survey", client_id: s.client_id || null, server_id: serverId });
          } else {
            throw e;
          }
        }
      }
      if (s.client_id) {
        await removeLocalSurvey(s.client_id);
      }
      toast.success("Survey deleted.");
      // refresh() catches any drift (e.g. another tab edited the list)
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
      // Roll back the optimistic removal by re-fetching authoritative state
      refresh();
    }
  };

  // Map: product_type → first template for that product type
  const templateByProductType = {};
  for (const t of templates) {
    if (!templateByProductType[t.product_type]) templateByProductType[t.product_type] = t;
  }
  const productTypesWithTemplate = Object.keys(templateByProductType).sort();
  const selectedTemplate = picker.productType ? templateByProductType[picker.productType] : null;

  return (
    <div className="space-y-6 fade-up" data-testid="surveys-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Site Surveys</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Surveys</h1>
          <p className="text-sm text-slate-500 mt-1">Capture site information offline · convert to a quote when ready.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <OnlineBadge online={online} />
          <button
            onClick={async () => { await syncNow(); refresh(); }}
            disabled={!online}
            className="h-9 px-3 border border-slate-300 hover:border-brand-blue hover:text-brand-blue text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="sync-now-button"
            title="Drain pending sync queue"
          >
            {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync now
          </button>
          <button
            onClick={onCreate}
            data-testid="new-survey-button"
            className="h-10 px-5 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark"
          >
            <Plus size={14} /> New survey
          </button>
        </div>
      </div>

      <div className="qb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Template</th>
                <th className="text-left px-4 py-3">Product type</th>
                <th className="text-left px-4 py-3">Postcode</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Sync</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="surveys-table-body">
              {merged.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-400">
                    No surveys yet. Start one to capture site information.
                  </td>
                </tr>
              )}
              {merged.map((s) => {
                const key = s.client_id || s.id;
                const created = (s.created_at || s.updated_at_local || "").slice(0, 19).replace("T", " ");
                const syncStatus = s.client_id ? (statusMap[s.client_id] || "synced") : "synced";
                return (
                  <tr key={key} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid="survey-row">
                    <td className="px-4 py-2.5 font-medium text-slate-900">
                      <div>{s.customer_name || "—"}</div>
                      {s.site_name && (
                        <div className="text-[11px] text-slate-500 font-normal">{s.site_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{s.template_name || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{s.product_type || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{s.postcode || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{created || "—"}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill value={s.status || "Draft"} />
                    </td>
                    <td className="px-4 py-2.5">
                      <SyncPill value={syncStatus} />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            const cid = s.client_id;
                            const sid = s.server_id || s.id;
                            if (cid) nav(`/surveys/edit?cid=${encodeURIComponent(cid)}${sid ? `&sid=${encodeURIComponent(sid)}` : ""}`);
                            else nav(`/surveys/edit?sid=${encodeURIComponent(sid)}`);
                          }}
                          data-testid="survey-open"
                          className="text-xs uppercase tracking-wider font-semibold text-brand-blue hover:underline"
                        >Open</button>
                        <button
                          onClick={() => deleteSurvey(s)}
                          data-testid="survey-delete"
                          className="text-xs uppercase tracking-wider font-semibold text-red-600 hover:underline inline-flex items-center gap-1"
                          title="Delete this survey"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {picker.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setPicker({ ...picker, open: false })}>
          <div className="bg-white max-w-md w-full p-5 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="template-picker">
            <h2 className="font-display text-xl font-bold mb-1">Start a new survey</h2>
            <p className="text-xs text-slate-500 mb-4">Please select what product you are surveying.</p>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Product type</label>
            <select
              value={picker.productType}
              onChange={(e) => setPicker({ ...picker, productType: e.target.value })}
              className="qb-input mt-1"
              data-testid="picker-product-type"
              autoFocus
            >
              <option value="">— pick a product type —</option>
              {productTypesWithTemplate.map((pt) => (
                <option key={pt} value={pt}>{pt}</option>
              ))}
            </select>
            {picker.productType && !selectedTemplate && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                No survey form exists for this product type yet. Ask an admin to create one in Settings → Surveys.
              </div>
            )}
            {selectedTemplate && (
              <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid="picker-template-summary">
                Form: <span className="font-semibold text-slate-900">{selectedTemplate.name}</span> · {(selectedTemplate.questions || []).length} question{(selectedTemplate.questions || []).length === 1 ? "" : "s"}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setPicker({ ...picker, open: false })} className="h-9 px-3 border border-slate-300 text-xs uppercase tracking-wider font-semibold">Cancel</button>
              <button
                onClick={() => { startNewSurvey(selectedTemplate.id); setPicker({ ...picker, open: false }); }}
                disabled={!selectedTemplate}
                className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
                data-testid="picker-start"
              >Start survey</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OnlineBadge({ online }) {
  if (online) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-green-100 text-green-700 font-semibold" data-testid="online-badge">
        <Wifi size={11} /> Online
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid="offline-badge">
      <WifiOff size={11} /> Offline
    </span>
  );
}

function StatusPill({ value }) {
  const map = {
    Draft: "bg-slate-200 text-slate-700",
    Completed: "bg-blue-100 text-blue-700",
    Converted: "bg-emerald-100 text-emerald-700",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${map[value] || "bg-slate-200 text-slate-700"}`}>{value}</span>
  );
}

function SyncPill({ value }) {
  if (value === "synced") {
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-green-700 font-semibold" data-testid="sync-synced"><CheckCircle2 size={12} /> Synced</span>;
  }
  if (value === "error") {
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-700 font-semibold" data-testid="sync-error"><AlertCircle size={12} /> Sync failed</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700 font-semibold" data-testid="sync-pending"><Loader2 size={12} className="animate-spin" /> Pending</span>;
}

// (icon helper removed)
